import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Shield,
  Plus,
  Trash2,
  Edit,
  Copy,
  ExternalLink,
  AlertCircle,
  Globe,
  Loader2,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Eye,
  EyeOff,
  Clock,
  CheckCircle2,
  XCircle,
  ArrowLeft,
} from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useIsMobile } from "@/hooks/useMobileUtils";
import { ssoApi } from "@/services/api";

const PROVIDER_OPTIONS = [
  {
    value: "saml",
    label: "SAML 2.0",
    description: "For enterprise identity providers like ADFS, Ping, etc.",
  },
  {
    value: "oidc",
    label: "OpenID Connect",
    description: "Generic OIDC provider",
  },
  {
    value: "google",
    label: "Google Workspace",
    description: "Sign in with Google",
  },
  {
    value: "microsoft",
    label: "Microsoft Entra ID",
    description: "Sign in with Microsoft (Azure AD)",
  },
  { value: "okta", label: "Okta", description: "Sign in with Okta" },
];

const DEFAULT_ATTRIBUTE_MAPPING = {
  email: "email",
  firstName: "given_name",
  lastName: "family_name",
  displayName: "name",
};

export const SSOSettings = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState("configurations");

  // Dialog states
  const [configDialog, setConfigDialog] = useState({
    open: false,
    config: null,
  });
  const [domainDialog, setDomainDialog] = useState({ open: false });
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    type: null,
    item: null,
  });

  // Form state
  const [formData, setFormData] = useState({});
  const [showSecret, setShowSecret] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  // ============================================
  // TanStack Query - Fetch All SSO Data
  // ============================================
  const { data: ssoData, isLoading: loading } = useQuery({
    queryKey: ["sso-settings"],
    queryFn: async () => {
      const [configsRes, domainsRes, attemptsRes] = await Promise.all([
        ssoApi.getConfigurations(),
        ssoApi.getDomains(),
        ssoApi.getLoginAttempts(20),
      ]);
      return {
        configs: configsRes.data.data || [],
        domains: domainsRes.data.data || [],
        attempts: attemptsRes.data.data || [],
      };
    },
    retry: false,
    meta: {
      onError: (err) => {
        if (err.response?.status !== 403) {
          toast.error("Failed to load SSO settings");
        }
      },
    },
  });

  const configs = ssoData?.configs || [];
  const domains = ssoData?.domains || [];
  const attempts = ssoData?.attempts || [];

  // ============================================
  // TanStack Query - Mutations
  // ============================================
  const saveConfigMutation = useMutation({
    mutationFn: (data) => ssoApi.saveConfiguration(data),
    onSuccess: () => {
      toast.success(
        configDialog.config ? "Configuration updated" : "Configuration created",
      );
      setConfigDialog({ open: false, config: null });
      queryClient.invalidateQueries({ queryKey: ["sso-settings"] });
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || "Failed to save configuration");
    },
  });

  const deleteConfigMutation = useMutation({
    mutationFn: (id) => ssoApi.deleteConfiguration(id),
    onSuccess: () => {
      toast.success("Configuration deleted");
      setDeleteDialog({ open: false, type: null, item: null });
      queryClient.invalidateQueries({ queryKey: ["sso-settings"] });
    },
    onError: () => {
      toast.error("Failed to delete configuration");
    },
  });

  const addDomainMutation = useMutation({
    mutationFn: ({ configId, domain }) => ssoApi.addDomain(configId, domain),
    onSuccess: () => {
      toast.success("Domain added. Please add the DNS record to verify.");
      setDomainDialog({ open: false });
      queryClient.invalidateQueries({ queryKey: ["sso-settings"] });
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || "Failed to add domain");
    },
  });

  const deleteDomainMutation = useMutation({
    mutationFn: (domain) => ssoApi.deleteDomain(domain),
    onSuccess: () => {
      toast.success(t("toast.domainRemoved"));
      setDeleteDialog({ open: false, type: null, item: null });
      queryClient.invalidateQueries({ queryKey: ["sso-settings"] });
    },
    onError: () => {
      toast.error("Failed to remove domain");
    },
  });

  const saving =
    saveConfigMutation.isPending ||
    deleteConfigMutation.isPending ||
    addDomainMutation.isPending ||
    deleteDomainMutation.isPending;

  const openConfigDialog = (config = null) => {
    if (config) {
      setFormData({
        providerType: config.provider_type,
        providerName: config.provider_name,
        isEnabled: config.is_enabled,
        isEnforced: config.is_enforced,
        // SAML
        samlEntityId: config.saml_entity_id,
        samlSsoUrl: config.saml_sso_url,
        samlSloUrl: config.saml_slo_url,
        samlCertificate:
          config.saml_certificate === "***configured***"
            ? ""
            : config.saml_certificate,
        samlMetadataUrl: config.saml_metadata_url,
        // OIDC
        oidcClientId: config.oidc_client_id,
        oidcClientSecret: "",
        oidcIssuerUrl: config.oidc_issuer_url,
        oidcScopes: config.oidc_scopes,
        // Common
        attributeMapping: config.attribute_mapping || DEFAULT_ATTRIBUTE_MAPPING,
        allowedDomains: config.allowed_domains?.join(", ") || "",
        autoProvisionUsers: config.auto_provision_users ?? true,
        defaultRole: config.default_role || "member",
        autoUpdateProfile: config.auto_update_profile ?? false,
        sessionDurationHours: config.session_duration_hours || 24,
      });
    } else {
      setFormData({
        providerType: "saml",
        providerName: "",
        isEnabled: false,
        isEnforced: false,
        samlEntityId: "",
        samlSsoUrl: "",
        samlSloUrl: "",
        samlCertificate: "",
        samlMetadataUrl: "",
        oidcClientId: "",
        oidcClientSecret: "",
        oidcIssuerUrl: "",
        oidcScopes: "openid email profile",
        attributeMapping: DEFAULT_ATTRIBUTE_MAPPING,
        allowedDomains: "",
        autoProvisionUsers: true,
        defaultRole: "member",
        autoUpdateProfile: false,
        sessionDurationHours: 24,
      });
    }
    setAdvancedOpen(false);
    setShowSecret(false);
    setConfigDialog({ open: true, config });
  };

  const handleSaveConfig = () => {
    // Parse allowed domains
    const allowedDomains = formData.allowedDomains
      ? formData.allowedDomains
          .split(",")
          .map((d) => d.trim().toLowerCase())
          .filter(Boolean)
      : null;

    saveConfigMutation.mutate({
      ...formData,
      allowedDomains,
    });
  };

  const handleDeleteConfig = () => {
    deleteConfigMutation.mutate(deleteDialog.item.id);
  };

  const handleTestConfig = async (configId) => {
    try {
      const result = await ssoApi.testConfiguration(configId);
      if (result.data.data.valid) {
        toast.success("Configuration is valid");
      } else {
        toast.error(`Issues found: ${result.data.data.issues.join(", ")}`);
      }
    } catch {
      toast.error("Test failed");
    }
  };

  const handleAddDomain = () => {
    addDomainMutation.mutate({
      configId: domainDialog.configId,
      domain: domainDialog.domain,
    });
  };

  const handleVerifyDomain = async (domain) => {
    try {
      const result = await ssoApi.verifyDomain(domain);
      if (result.data.data.verified) {
        toast.success("Domain verified successfully");
        queryClient.invalidateQueries({ queryKey: ["sso-settings"] });
      } else {
        toast.error(
          "Verification failed. Please ensure the DNS record is properly configured.",
        );
      }
    } catch {
      toast.error("Verification failed");
    }
  };

  const handleDeleteDomain = () => {
    deleteDomainMutation.mutate(deleteDialog.item);
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const getProviderLabel = (type) =>
    PROVIDER_OPTIONS.find((p) => p.value === type)?.label || type;

  const renderOIDCConfigurationTitle = () => {
    if (formData?.providerType === "google") {
      return t("settings.sso.providerTypes.google");
    }
    if (formData?.providerType === "microsoft") {
      return t("settings.sso.providerTypes.microsoft");
    }
    return formData?.providerType === "okta"
      ? t("settings.sso.providerTypes.okta")
      : t("settings.sso.providerTypes.oidc");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-100">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20 sm:pb-0">
      {/* Page header with back link */}
      <div className="flex items-center gap-4">
        <Link
          to="/settings"
          className="flex items-center justify-center h-10 w-10 sm:h-8 sm:w-8 rounded-lg border hover:bg-accent transition-colors touch-target"
        >
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("settings.sso.title")}
          </h1>
          <p className="text-muted-foreground">
            {t("settings.sso.configureSamlOidc")}
          </p>
        </div>
      </div>

      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-lg font-medium">{t("settings.sso.providers.title")}</h3>
          <p className="text-sm text-muted-foreground">
            {t("settings.sso.configureDesc")}
          </p>
        </div>
        <Button
          onClick={() => openConfigDialog()}
          size={isMobile ? "mobile" : "default"}
          className={isMobile ? "w-full" : ""}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Provider
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="configurations">
            {t("settings.sso.tabs.providers")}
          </TabsTrigger>
          <TabsTrigger value="domains">
            {t("settings.sso.tabs.domains")}
          </TabsTrigger>
          <TabsTrigger value="audit">
            {t("settings.sso.tabs.loginHistory")}
          </TabsTrigger>
        </TabsList>

        {/* Providers Tab */}
        <TabsContent value="configurations" className="space-y-4">
          {configs.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Shield className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">
                  {t("settings.sso.providers.noProviders")}
                </h3>
                <p className="text-muted-foreground text-center mb-4">
                  {t("settings.sso.providers.noProvidersDesc")}
                </p>
                <Button onClick={() => openConfigDialog()}>
                  <Plus className="h-4 w-4 mr-2" />
                  {t("settings.sso.providers.addProvider")}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {configs.map((config) => (
                <Card key={config.id}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div
                          className={`p-2 rounded-lg ${
                            config.is_enabled
                              ? "bg-green-100 dark:bg-green-900"
                              : "bg-gray-100 dark:bg-gray-800"
                          }`}
                        >
                          <Shield
                            className={`h-5 w-5 ${
                              config.is_enabled
                                ? "text-green-600"
                                : "text-gray-500"
                            }`}
                          />
                        </div>
                        <div>
                          <CardTitle className="text-lg flex items-center gap-2">
                            {config.provider_name}
                            <Badge
                              variant={
                                config.is_enabled ? "success" : "secondary"
                              }
                            >
                              {config.is_enabled
                                ? t("settings.sso.providers.enabled")
                                : t("settings.sso.providers.disabled")}
                            </Badge>
                            {config.is_enforced && (
                              <Badge variant="destructive">
                                {t("settings.sso.providers.enforced")}
                              </Badge>
                            )}
                          </CardTitle>
                          <CardDescription>
                            {getProviderLabel(config.provider_type)}
                          </CardDescription>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleTestConfig(config.id)}
                        >
                          {t("common.test")}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openConfigDialog(config)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setDeleteDialog({
                              open: true,
                              type: "config",
                              item: config,
                            })
                          }
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      {config.provider_type === "saml" ? (
                        <>
                          <div>
                            <span className="text-muted-foreground">
                              {t("settings.sso.entityId")}
                            </span>
                            <p className="font-mono text-xs truncate">
                              {config.saml_entity_id}
                            </p>
                          </div>
                          <div>
                            <span className="text-muted-foreground">
                              {t("settings.sso.url")}
                            </span>
                            <p className="font-mono text-xs truncate">
                              {config.saml_sso_url}
                            </p>
                          </div>
                        </>
                      ) : (
                        <>
                          <div>
                            <span className="text-muted-foreground">
                              {t("embed.clientId")}
                            </span>
                            <p className="font-mono text-xs truncate">
                              {config.oidc_client_id}
                            </p>
                          </div>
                          <div>
                            <span className="text-muted-foreground">
                              {t("common.issuer")}
                            </span>
                            <p className="font-mono text-xs truncate">
                              {config.oidc_issuer_url || "Default"}
                            </p>
                          </div>
                        </>
                      )}
                      <div>
                        <span className="text-muted-foreground">
                          {t("settings.sso.autoProvision")}
                        </span>
                        <p>
                          {config.auto_provision_users ? "Enabled" : "Disabled"}
                        </p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">
                          {t("common.lastUsed")}
                        </span>
                        <p>
                          {config.last_used_at
                            ? new Date(config.last_used_at).toLocaleDateString()
                            : "Never"}
                        </p>
                      </div>
                    </div>

                    {config.provider_type === "saml" && (
                      <div className="mt-4 p-3 bg-muted rounded-lg">
                        <p className="text-sm font-medium mb-2">
                          {t("settings.sso.serviceProviderDetails")}
                        </p>
                        <div className="space-y-2 text-xs font-mono">
                          <div className="flex items-center gap-2">
                            <span className="text-muted-foreground w-24">
                              ACS URL:
                            </span>
                            <code className="flex-1 truncate">{`${window.location.origin}/api/sso/saml/${config.id}/callback`}</code>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                copyToClipboard(
                                  `${window.location.origin}/api/sso/saml/${config.id}/callback`,
                                  "ACS URL",
                                )
                              }
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-muted-foreground w-24">
                              Entity ID:
                            </span>
                            <code className="flex-1 truncate">{`${window.location.origin}/api/sso/saml/${config.id}/metadata`}</code>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                copyToClipboard(
                                  `${window.location.origin}/api/sso/saml/${config.id}/metadata`,
                                  "Entity ID",
                                )
                              }
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-muted-foreground w-24">
                              Metadata:
                            </span>
                            <a
                              href={`${window.location.origin}/api/sso/saml/${config.id}/metadata`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary hover:underline flex items-center gap-1"
                            >
                              Download <ExternalLink className="h-3 w-3" />
                            </a>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Domains Tab */}
        <TabsContent value="domains" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>{t("settings.sso.domains.title")}</CardTitle>
                  <CardDescription>
                    {t("settings.sso.domains.description")}
                  </CardDescription>
                </div>
                <Button
                  onClick={() =>
                    setDomainDialog({
                      open: true,
                      domain: "",
                      configId: configs[0]?.id,
                    })
                  }
                  disabled={configs.length === 0}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t("settings.sso.domains.addDomain")}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {domains.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Globe className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>{t("settings.sso.domains.noDomains")}</p>
                  <p className="text-sm">
                    {t("settings.sso.domains.noDomainsDesc")}
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("settings.sso.domains.domain")}</TableHead>
                      <TableHead>{t("settings.sso.audit.provider")}</TableHead>
                      <TableHead>{t("common.status")}</TableHead>
                      <TableHead>
                        {t("settings.sso.domains.verified")}
                      </TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {domains.map((domain) => (
                      <TableRow key={domain.id}>
                        <TableCell className="font-mono">
                          {domain.domain}
                        </TableCell>
                        <TableCell>{domain.provider_name}</TableCell>
                        <TableCell>
                          <Badge
                            variant={domain.is_verified ? "success" : "warning"}
                          >
                            {domain.is_verified
                              ? t("settings.sso.domains.verified")
                              : t("settings.sso.domains.pending")}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {domain.verified_at
                            ? new Date(domain.verified_at).toLocaleDateString()
                            : "-"}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2 justify-end">
                            {!domain.is_verified && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  handleVerifyDomain(domain.domain)
                                }
                              >
                                <RefreshCw className="h-4 w-4 mr-1" />
                                {t("settings.sso.domains.verify")}
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                setDeleteDialog({
                                  open: true,
                                  type: "domain",
                                  item: domain.domain,
                                })
                              }
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {t("settings.sso.domains.verificationHelp")}
            </AlertDescription>
          </Alert>
        </TabsContent>

        {/* Audit Tab */}
        <TabsContent value="audit" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t("settings.sso.audit.title")}</CardTitle>
              <CardDescription>
                {t("settings.sso.audit.description")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {attempts.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>{t("settings.sso.audit.noAttempts")}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("settings.sso.audit.timestamp")}</TableHead>
                      <TableHead>{t("common.email")}</TableHead>
                      <TableHead>{t("settings.sso.audit.provider")}</TableHead>
                      <TableHead>{t("common.status")}</TableHead>
                      <TableHead>{t("common.details")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {attempts.map((attempt) => (
                      <TableRow key={attempt.id}>
                        <TableCell className="text-sm">
                          {new Date(attempt.created_at).toLocaleString()}
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {attempt.email}
                        </TableCell>
                        <TableCell>{attempt.provider_name || "-"}</TableCell>
                        <TableCell>
                          {attempt.success ? (
                            <Badge variant="success" className="gap-1">
                              <CheckCircle2 className="h-3 w-3" />
                              Success
                            </Badge>
                          ) : (
                            <Badge variant="destructive" className="gap-1">
                              <XCircle className="h-3 w-3" />
                              Failed
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {attempt.was_provisioned && (
                            <Badge variant="outline">{t("team.newUser")}</Badge>
                          )}
                          {attempt.error_message && (
                            <span className="text-destructive">
                              {attempt.error_message}
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Configuration Dialog */}
      <Dialog
        open={configDialog.open}
        onOpenChange={(open) => setConfigDialog({ open, config: null })}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {configDialog.config ? "Edit SSO Provider" : "Add SSO Provider"}
            </DialogTitle>
            <DialogDescription>
              Configure your identity provider for single sign-on
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Provider Type */}
            <div className="space-y-2">
              <Label>{t("settings.sso.form.providerType")}</Label>
              <Select
                value={formData.providerType}
                onValueChange={(value) =>
                  setFormData({ ...formData, providerType: value })
                }
                disabled={!!configDialog.config}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PROVIDER_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      <div>
                        <div className="font-medium">{opt.label}</div>
                        <div className="text-xs text-muted-foreground">
                          {opt.description}
                        </div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Provider Name */}
            <div className="space-y-2">
              <Label>{t("settings.sso.form.displayName")}</Label>
              <Input
                placeholder={t("settings.sso.form.displayNamePlaceholder")}
                value={formData.providerName || ""}
                onChange={(e) =>
                  setFormData({ ...formData, providerName: e.target.value })
                }
              />
            </div>

            <Separator />

            {/* SAML Configuration */}
            {formData.providerType === "saml" && (
              <div className="space-y-4">
                <h4 className="font-medium">
                  {t("settings.sso.providerTypes.saml")}
                </h4>

                <div className="space-y-2">
                  <Label>{t("settings.sso.form.idpEntityId")}</Label>
                  <Input
                    placeholder="https://idp.example.com/..."
                    value={formData.samlEntityId || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, samlEntityId: e.target.value })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t("settings.sso.form.idpSsoUrl")}</Label>
                  <Input
                    placeholder="https://idp.example.com/sso"
                    value={formData.samlSsoUrl || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, samlSsoUrl: e.target.value })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t("settings.sso.form.idpCertificate")}</Label>
                  <Textarea
                    placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
                    className="font-mono text-xs"
                    rows={6}
                    value={formData.samlCertificate || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        samlCertificate: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t("settings.sso.form.metadataUrl")}</Label>
                  <Input
                    placeholder="https://idp.example.com/metadata"
                    value={formData.samlMetadataUrl || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        samlMetadataUrl: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
            )}

            {/* OIDC Configuration */}
            {["oidc", "google", "microsoft", "okta"].includes(
              formData.providerType,
            ) && (
              <div className="space-y-4">
                <h4 className="font-medium">
                  {renderOIDCConfigurationTitle()}
                </h4>

                <div className="space-y-2">
                  <Label>{t("settings.sso.form.clientId")}</Label>
                  <Input
                    placeholder={t("settings.sso.form.clientIdPlaceholder")}
                    value={formData.oidcClientId || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, oidcClientId: e.target.value })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t("settings.sso.form.clientSecret")}</Label>
                  <div className="relative">
                    <Input
                      type={showSecret ? "text" : "password"}
                      placeholder={
                        configDialog.config
                          ? "••••••••••••"
                          : t("settings.sso.form.clientIdPlaceholder")
                      }
                      value={formData.oidcClientSecret || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          oidcClientSecret: e.target.value,
                        })
                      }
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-2 top-1/2 -translate-y-1/2"
                      onClick={() => setShowSecret(!showSecret)}
                    >
                      {showSecret ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>

                {formData.providerType === "okta" && (
                  <div className="space-y-2">
                    <Label>{t("settings.sso.form.oktaDomain")}</Label>
                    <Input
                      placeholder={t("settings.sso.form.oktaDomainPlaceholder")}
                      value={formData.oidcIssuerUrl || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          oidcIssuerUrl: e.target.value,
                        })
                      }
                    />
                  </div>
                )}

                {formData.providerType === "microsoft" && (
                  <div className="space-y-2">
                    <Label>{t("settings.sso.form.tenantId")}</Label>
                    <Input
                      placeholder="common, organisations, or your tenant ID"
                      value={formData.oidcIssuerUrl || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          oidcIssuerUrl: e.target.value,
                        })
                      }
                    />
                  </div>
                )}
              </div>
            )}

            <Separator />

            {/* Status & Enforcement */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>{t("settings.sso.form.enableSso")}</Label>
                  <p className="text-sm text-muted-foreground">
                    {t("settings.sso.form.enableSsoDesc")}
                  </p>
                </div>
                <Switch
                  checked={formData.isEnabled || false}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, isEnabled: checked })
                  }
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-destructive">
                    {t("settings.sso.form.enforceSso")}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {t("settings.sso.form.enforceSsoDesc")}
                  </p>
                </div>
                <Switch
                  checked={formData.isEnforced || false}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, isEnforced: checked })
                  }
                />
              </div>
            </div>

            {/* Advanced Settings */}
            <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" className="w-full justify-between">
                  {t("common.advancedSettings")}
                  {advancedOpen ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>{t("settings.sso.form.allowedDomains")}</Label>
                  <Input
                    placeholder={t(
                      "settings.sso.form.allowedDomainsPlaceholder",
                    )}
                    value={formData.allowedDomains || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        allowedDomains: e.target.value,
                      })
                    }
                  />
                  <p className="text-xs text-muted-foreground">
                    {t("settings.sso.form.allowedDomainsHelp")}
                  </p>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label>{t("settings.sso.form.autoProvision")}</Label>
                    <p className="text-sm text-muted-foreground">
                      {t("settings.sso.form.autoProvisionDesc")}
                    </p>
                  </div>
                  <Switch
                    checked={formData.autoProvisionUsers ?? true}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, autoProvisionUsers: checked })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t("settings.sso.form.defaultRole")}</Label>
                  <Select
                    value={formData.defaultRole || "member"}
                    onValueChange={(value) =>
                      setFormData({ ...formData, defaultRole: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="member">
                        {t("team.roles.member")}
                      </SelectItem>
                      <SelectItem value="admin">
                        {t("team.roles.admin")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label>{t("settings.sso.form.updateProfile")}</Label>
                    <p className="text-sm text-muted-foreground">
                      {t("settings.sso.form.updateProfileDesc")}
                    </p>
                  </div>
                  <Switch
                    checked={formData.autoUpdateProfile || false}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, autoUpdateProfile: checked })
                    }
                  />
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfigDialog({ open: false, config: null })}
            >
              {t("common.cancel")}
            </Button>
            <Button onClick={handleSaveConfig} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {configDialog.config
                ? t("common.saveChanges")
                : t("settings.sso.form.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Domain Dialog */}
      <Dialog
        open={domainDialog.open}
        onOpenChange={(open) => setDomainDialog({ open })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("settings.sso.domains.addTitle")}</DialogTitle>
            <DialogDescription>
              {t("settings.sso.domains.addDesc")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t("settings.sso.domains.domain")}</Label>
              <Input
                placeholder={t("settings.sso.domains.domainPlaceholder")}
                value={domainDialog.domain || ""}
                onChange={(e) =>
                  setDomainDialog({ ...domainDialog, domain: e.target.value })
                }
              />
            </div>

            <div className="space-y-2">
              <Label>{t("settings.sso.form.providerType")}</Label>
              <Select
                value={domainDialog.configId}
                onValueChange={(value) =>
                  setDomainDialog({ ...domainDialog, configId: value })
                }
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={t("settings.sso.form.selectProvider")}
                  />
                </SelectTrigger>
                <SelectContent>
                  {configs.map((config) => (
                    <SelectItem key={config.id} value={config.id}>
                      {config.provider_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDomainDialog({ open: false })}
            >
              {t("common.cancel")}
            </Button>
            <Button
              onClick={handleAddDomain}
              disabled={
                saving || !domainDialog.domain || !domainDialog.configId
              }
            >
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t("settings.sso.domains.addDomain")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onOpenChange={(open) =>
          setDeleteDialog({ open, type: null, item: null })
        }
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("settings.sso.deleteConfirm.title")}</DialogTitle>
            <DialogDescription>
              {deleteDialog.type === "config"
                ? t("settings.sso.deleteConfirm.description")
                : t("settings.sso.domains.removeDesc")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                setDeleteDialog({ open: false, type: null, item: null })
              }
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={
                deleteDialog.type === "config"
                  ? handleDeleteConfig
                  : handleDeleteDomain
              }
              disabled={saving}
            >
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t("common.delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SSOSettings;
